import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { actOnCard, peekDevice, type BoardPeek } from '../api/dashboard'
import { toast } from '../ui/notify'

/**
 * Cele doua unelte care traiesc PE tablou, langa cifre - `peek.js` si `act.js`.
 *
 * PANOUL MASINII (`Dashboard::peek`)
 * -----------------------------------------------------------------------------
 * A treia intrebare din cele patru pe care le pune omul cand ceva merge prost
 * e "unde anume". Tabloul raspundea cu o legatura: apesi, pleci de pe tablou,
 * ajungi pe o lista de trei sute de randuri, si cauti acolo masina pe care
 * tocmai apasasesi. Panoul vine langa cifra; drumul catre fisa ramane, scris
 * pe el.
 *
 * ACTIUNILE DE PE CARD (`Dashboard::act`)
 * -----------------------------------------------------------------------------
 * Un buton pe un tablou de bord e apasat din trecere. De-aia fereastra
 * dinainte nu intreaba "esti sigur?", care nu inseamna nimic, ci SPUNE ce
 * urmeaza sa se intample si pe ce. Nimic din ce se trimite de aici nu e
 * crezut de server: pleaca doar numele cardului si al actiunii.
 *
 * UN SINGUR ASCULTATOR PE DOCUMENT, NU CATE UNUL PE RAND
 * -----------------------------------------------------------------------------
 * Randurile si butoanele vin in HTML-ul cardului, gata scris de server
 * (`CardMarkup`), pus cu `dangerouslySetInnerHTML` - React nu le stie, deci nu
 * le poate lega. Ascultatorul sta pe document, ca in `peek.js`/`act.js`, si se
 * uita pe ce s-a apasat.
 */
export function BoardTools({ days, klass }: { days: number; klass: string }) {
  const [peek, setPeek] = useState<{
    device: number
    data: BoardPeek | null
    error: string
  } | null>(null)
  const [act, setAct] = useState<{
    card: string
    action: string
    label: string
    ask: string
  } | null>(null)
  const [busy, setBusy] = useState(false)
  /* Cine a deschis panoul: acolo se intoarce focarul la inchidere, altfel
     omul care navigheaza cu tastatura ramane la inceputul paginii. */
  const opener = useRef<HTMLElement | null>(null)
  const yes = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      const target = ev.target as HTMLElement | null
      if (!target || typeof target.closest !== 'function') return

      const row = target.closest<HTMLElement>('.odb-row-peek')
      if (row) {
        ev.preventDefault()
        const device = parseInt(row.getAttribute('data-device') ?? '', 10)
        if (!device) return
        opener.current = row
        /* Se scrie ceva CAT SE ASTEAPTA: un panou care apare gol si se umple
           dupa o secunda arata a defect in exact secunda aia. */
        setPeek({ device, data: null, error: '' })
        peekDevice(device)
          .then((data) => setPeek((cur) => (cur?.device === device ? { device, data, error: '' } : cur)))
          .catch(() => {
            /* 404 inseamna si "nu exista", si "nu e a firmei tale" - dinadins
               acelasi raspuns, ca sa nu se poata numara masinile altcuiva. Pe
               ecran se scrie ce poate face omul, nu ce s-a intamplat in server. */
            setPeek((cur) =>
              cur?.device === device
                ? {
                    device,
                    data: null,
                    error: 'This machine could not be opened from here.',
                  }
                : cur,
            )
          })
        return
      }

      const button = target.closest<HTMLElement>('.odb-act')
      if (button) {
        ev.preventDefault()
        setAct({
          card: button.getAttribute('data-card') ?? '',
          action: button.getAttribute('data-action') ?? '',
          label: (button.textContent ?? '').trim(),
          ask: button.getAttribute('data-ask') ?? '',
        })
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== 'Escape') return
      setAct(null)
      setPeek((cur) => {
        if (cur) opener.current?.focus()
        return null
      })
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    if (act) yes.current?.focus()
  }, [act])

  function shutPeek() {
    setPeek(null)
    opener.current?.focus()
    opener.current = null
  }

  async function run() {
    if (!act) return
    const going = act
    setAct(null)
    setBusy(true)
    try {
      const out = await actOnCard(going.card, going.action, days, klass)
      toast(out.said || 'Done.', 'Done', 'success')
    } catch (err) {
      /* Si refuzul se spune, cu motivul serverului: "n-ai voie aici" e o
         informatie, iar tacerea nu e. */
      const said = err instanceof ApiError ? saidOf(err) : ''
      toast(said || 'That did not go through.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="odb-acts-cfg" hidden id="odbActs" data-url="/api/v2/dashboard/act"></div>
      <div className="odb-peek" id="odbPeek" hidden={!peek} data-url="/api/v2/dashboard/peek">
        <button type="button" className="odb-peek-close" id="odbPeekClose" aria-label="Close" onClick={shutPeek}>
          &times;
        </button>
        <div className="odb-peek-body" id="odbPeekBody">
          {peek && !peek.data && peek.error === '' && 'Reading the machine...'}
          {peek && peek.error !== '' && peek.error}
          {peek?.data && <PeekBody peek={peek.data} />}
        </div>
      </div>
      {/* Aceeasi fereastra ca a constructorului (`odb-dialog`), cu textul
          actiunii: ce se intampla, si pe ce. */}
      <div className="odb-dialog" id="odbActDialog" hidden={!act} onClick={(e) => e.target === e.currentTarget && setAct(null)}>
        <div className="odb-dialog-box" role="dialog" aria-modal="true" aria-labelledby="odbActTitle">
          <h4 className="odb-dialog-title" id="odbActTitle">
            {act?.label}
          </h4>
          <p className="odb-dialog-text">{act?.ask}</p>
          <div className="odb-dialog-foot">
            <button type="button" className="odb-btn" onClick={() => setAct(null)}>
              Cancel
            </button>
            <button type="button" className="odb-btn odb-btn-main" ref={yes} disabled={busy} onClick={() => void run()}>
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

/** Panoul, ca `version-3/dashboard/peek.php`. */
function PeekBody({ peek }: { peek: BoardPeek }) {
  return (
    <>
      <div className="odb-peek-head">
        <span className="odb-peek-name">{peek.name}</span>
        {peek.seen !== '' && <span className="odb-peek-seen">last spoke {peek.seen}</span>}
        {peek.agent !== '' && <span className="odb-peek-seen">agent {peek.agent}</span>}
      </div>
      {peek.facts.length === 0 ? (
        /* Nicio bucata n-a raspuns. Se spune, in loc sa ramana un panou gol
           care arata a defect. */
        <p className="odb-peek-empty">This machine has nothing recorded yet beyond its name.</p>
      ) : (
        <div className="odb-peek-facts">
          {peek.facts.map((fact, i) => (
            <div className={`odb-peek-fact odb-t-${fact.tone}`} key={i}>
              <span className="odb-peek-label">{fact.label}</span>
              <span className="odb-peek-value">{fact.value}</span>
            </div>
          ))}
        </div>
      )}
      <a className="odb-peek-go" href={`/app/inventoree/device/${peek.id}`}>
        Open the full record <i className="fas fa-arrow-right" aria-hidden="true"></i>
      </a>
    </>
  )
}

/** `said` din raspunsul de 400 al lui `dashboard/act`, cand e unul. */
function saidOf(err: ApiError): string {
  const said = err.details?.said
  return typeof said === 'string' ? said : err.message
}
